import * as React from 'react'
import styled from 'styled-components'
import { screenSizes } from '@src/styles/theme'
import { JsonSchema, ConfigUiHints } from '../../types'
import { hintForPath, humanize, schemaType, isSensitivePath } from '../../utils/hint-for-path'
import { TextField } from './fields/TextField'
import { NumberField } from './fields/NumberField'
import { BooleanField } from './fields/BooleanField'
import { SelectField } from './fields/SelectField'
import { SensitiveField } from './fields/SensitiveField'
import { ObjectField } from './fields/ObjectField'
import { ArrayField } from './fields/ArrayField'

export interface SchemaFormProps {
  schema: JsonSchema
  value: Record<string, unknown>
  hints: ConfigUiHints
  path: Array<string | number>
  onChange: (path: Array<string | number>, value: unknown) => void
  disabled?: boolean
}

const AdvancedToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid ${(props) => props.theme.listDesc};
  border-radius: ${(props) => props.theme.radius};
  color: ${(props) => props.theme.listDesc};
  font-size: 12px;
  padding: 6px 12px;
  margin-top: 8px;
  cursor: pointer;
  min-height: 44px;

  &:hover {
    color: ${(props) => props.theme.white};
    border-color: ${(props) => props.theme.white};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${(props) => props.theme.accent};
    outline: none;
  }

  @media (max-width: ${screenSizes.XS}px) {
    width: 100%;
    justify-content: center;
  }
`

/**
 * Resolves anyOf/oneOf unions to a single effective schema.
 * Filters out null-only variants, collapses single-variant unions,
 * and converts all-literal unions into enum schemas.
 * Matches the Control UI pattern at config-form.node.ts:63-137.
 */
function resolveUnion(schema: JsonSchema): JsonSchema {
  const variants = schema.anyOf ?? schema.oneOf
  if (!variants) return schema

  const nonNull = variants.filter(
    (v) => !(v.type === 'null' || (Array.isArray(v.type) && v.type.includes('null')))
  )

  if (nonNull.length === 1) {
    return { ...schema, ...nonNull[0], anyOf: undefined, oneOf: undefined }
  }

  const extractLiteral = (v: JsonSchema): unknown | undefined => {
    if (v.const !== undefined) return v.const
    if (v.enum && v.enum.length === 1) return v.enum[0]
    return undefined
  }
  const literals = nonNull.map(extractLiteral)
  if (literals.every((v) => v !== undefined) && literals.length > 0) {
    return { ...schema, enum: literals, anyOf: undefined, oneOf: undefined }
  }

  // Mixed primitive types: collapse to the dominant type
  const primitiveTypes = new Set(
    nonNull.map((v) => schemaType(v)).filter(Boolean)
  )
  const normalized = new Set(
    [...primitiveTypes].map((v) => (v === 'integer' ? 'number' : v))
  )
  if ([...normalized].every((v) => ['string', 'number', 'boolean'].includes(v as string))) {
    if (normalized.has('boolean') && normalized.size === 1) {
      return { ...schema, type: 'boolean', anyOf: undefined, oneOf: undefined }
    }
    const inputType = normalized.has('number') && !normalized.has('string') ? 'number' : 'string'
    return { ...schema, type: inputType, anyOf: undefined, oneOf: undefined }
  }

  return schema
}

function renderField(
  key: string,
  fieldSchema: JsonSchema,
  value: unknown,
  path: Array<string | number>,
  hints: ConfigUiHints,
  onChange: (path: Array<string | number>, value: unknown) => void,
  disabled?: boolean
) {
  const fieldPath = [...path, key]
  const hint = hintForPath(fieldPath, hints)
  const effective = resolveUnion(fieldSchema)
  const label = hint?.label ?? effective.title ?? humanize(key)
  const type = schemaType(effective)

  if (effective.enum) {
    return (
      <SelectField
        key={key}
        label={label}
        value={value != null ? String(value) : ''}
        onChange={(v) => onChange(fieldPath, v)}
        hint={hint}
        schema={effective}
        disabled={disabled}
      />
    )
  }

  if (type === 'object') {
    const objValue = (value && typeof value === 'object' && !Array.isArray(value))
      ? (value as Record<string, unknown>)
      : {}
    return (
      <ObjectField
        key={key}
        label={label}
        schema={effective}
        value={objValue}
        hints={hints}
        path={fieldPath}
        hint={hint}
        onChange={onChange}
        disabled={disabled}
      />
    )
  }

  if (type === 'array') {
    const arrValue = Array.isArray(value) ? value : []
    return (
      <ArrayField
        key={key}
        label={label}
        schema={effective}
        value={arrValue}
        hints={hints}
        path={fieldPath}
        hint={hint}
        onChange={onChange}
        disabled={disabled}
      />
    )
  }

  if (type === 'boolean') {
    const checked = typeof value === 'boolean' ? value : false
    return (
      <BooleanField
        key={key}
        label={label}
        value={checked}
        onChange={(v) => onChange(fieldPath, v)}
        hint={hint}
        schema={effective}
        disabled={disabled}
      />
    )
  }

  if (type === 'number' || type === 'integer') {
    return (
      <NumberField
        key={key}
        label={label}
        value={typeof value === 'number' ? value : 0}
        onChange={(v) => onChange(fieldPath, v)}
        hint={hint}
        schema={effective}
        disabled={disabled}
      />
    )
  }

  if (hint?.sensitive || isSensitivePath(fieldPath)) {
    return (
      <SensitiveField
        key={key}
        label={label}
        value={value != null ? String(value) : ''}
        onChange={(v) => onChange(fieldPath, v)}
        hint={hint}
        schema={effective}
        disabled={disabled}
      />
    )
  }

  return (
    <TextField
      key={key}
      label={label}
      value={value != null ? String(value) : ''}
      onChange={(v) => onChange(fieldPath, v)}
      hint={hint}
      schema={effective}
      disabled={disabled}
    />
  )
}

export function SchemaForm({ schema, value, hints, path, onChange, disabled }: SchemaFormProps) {
  const [showAdvanced, setShowAdvanced] = React.useState(false)

  const props = schema.properties ?? {}
  const entries = Object.entries(props)

  const sorted = entries.sort((a, b) => {
    const orderA = hintForPath([...path, a[0]], hints)?.order ?? 0
    const orderB = hintForPath([...path, b[0]], hints)?.order ?? 0
    if (orderA !== orderB) return orderA - orderB
    return a[0].localeCompare(b[0])
  })

  const standard: typeof sorted = []
  const advanced: typeof sorted = []

  for (const entry of sorted) {
    const hint = hintForPath([...path, entry[0]], hints)
    if (hint?.advanced) {
      advanced.push(entry)
    } else {
      standard.push(entry)
    }
  }

  return (
    <>
      {standard.map(([key, fieldSchema]) =>
        renderField(key, fieldSchema, value[key], path, hints, onChange, disabled)
      )}
      {advanced.length > 0 && (
        <>
          <AdvancedToggle type="button" onClick={() => setShowAdvanced(!showAdvanced)}>
            {showAdvanced ? '\u25BE Hide advanced' : '\u25B8 Show advanced'} ({advanced.length})
          </AdvancedToggle>
          {showAdvanced &&
            advanced.map(([key, fieldSchema]) =>
              renderField(key, fieldSchema, value[key], path, hints, onChange, disabled)
            )}
        </>
      )}
    </>
  )
}
